/**
 * Auth Domain Seed Data for E2E Testing
 *
 * Contains seed data for testing Authentication & Authorization scenarios (Issue #277):
 * - API Keys
 * - Sessions
 * - MFA Configurations
 * - SSO Configurations
 * - Device Trust Records
 *
 * @module seed/domains/auth
 * @see Issue #277 for Authentication & Authorization E2E scenarios
 */

import { seedId } from '../utils';
import { BUSINESS_IDS, REALM_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// API KEY IDs (Issue #277 - Scenarios 76-78)
// =============================================================================

export const API_KEY_IDS = {
  // Test API keys for E2E scenarios
  E2E_TEST_KEY_1: seedId('apikey-e2e-test-1', 1),
  E2E_TEST_KEY_2: seedId('apikey-e2e-test-2', 2),
  E2E_ROTATE_KEY: seedId('apikey-e2e-rotate', 3),
  E2E_REVOKE_KEY: seedId('apikey-e2e-revoke', 4),
  // Production-like API keys
  RACHEL_API_KEY: seedId('apikey-rachel', 1),
  SUPER_ADMIN_API_KEY: seedId('apikey-superadmin', 1),
  SERVICE_ACCOUNT_KEY: seedId('apikey-service', 1),
} as const;

export type ApiKeyId = (typeof API_KEY_IDS)[keyof typeof API_KEY_IDS];

// =============================================================================
// SESSION IDs (Issue #277 - Scenarios 69-70)
// =============================================================================

export const SESSION_IDS = {
  // Test sessions for E2E scenarios
  E2E_ACTIVE_SESSION: seedId('session-e2e-active', 1),
  E2E_TIMEOUT_SESSION: seedId('session-e2e-timeout', 2),
  // Production-like sessions
  RACHEL_SESSION: seedId('session-rachel', 1),
  SUPER_ADMIN_SESSION: seedId('session-superadmin', 1),
} as const;

export type SessionId = (typeof SESSION_IDS)[keyof typeof SESSION_IDS];

// =============================================================================
// MFA CONFIGURATION IDs (Issue #277 - Scenarios 66-68)
// =============================================================================

export const MFA_CONFIG_IDS = {
  // Test MFA configurations for E2E scenarios
  E2E_MFA_ENABLED: seedId('mfa-e2e-enabled', 1),
  E2E_MFA_DISABLED: seedId('mfa-e2e-disabled', 2),
  E2E_MFA_ENROLLMENT: seedId('mfa-e2e-enrollment', 3),
  // Production-like MFA configs
  RACHEL_MFA: seedId('mfa-rachel', 1),
  SUPER_ADMIN_MFA: seedId('mfa-superadmin', 1),
} as const;

export type MfaConfigId = (typeof MFA_CONFIG_IDS)[keyof typeof MFA_CONFIG_IDS];

// =============================================================================
// MFA BACKUP CODE IDs (Issue #277 - Scenario 68)
// =============================================================================

export const MFA_BACKUP_CODE_IDS = {
  E2E_BACKUP_CODE_SET: seedId('mfa-backup-e2e', 1),
  RACHEL_BACKUP_CODES: seedId('mfa-backup-rachel', 1),
  SUPER_ADMIN_BACKUP_CODES: seedId('mfa-backup-superadmin', 1),
} as const;

export type MfaBackupCodeId = (typeof MFA_BACKUP_CODE_IDS)[keyof typeof MFA_BACKUP_CODE_IDS];

// =============================================================================
// SSO CONFIGURATION IDs (Issue #277 - Scenarios 79-80)
// =============================================================================

export const SSO_CONFIG_IDS = {
  // SAML configurations
  E2E_SAML_CONFIG: seedId('sso-saml-e2e', 1),
  OPSMATRIX_SAML: seedId('sso-saml-opsmatrix', 1),
  // OIDC configurations
  E2E_OIDC_CONFIG: seedId('sso-oidc-e2e', 2),
  OPSMATRIX_OIDC: seedId('sso-oidc-opsmatrix', 2),
} as const;

export type SsoConfigId = (typeof SSO_CONFIG_IDS)[keyof typeof SSO_CONFIG_IDS];

// =============================================================================
// E2E AUTH TEST USER IDs (Issue #277)
// Dedicated test users for auth E2E scenarios
// =============================================================================

export const AUTH_TEST_USER_IDS = {
  // User with MFA enabled for MFA verification tests
  E2E_USER_MFA_ENABLED: seedId('user-e2e-mfa-enabled', 100),
  // User without MFA for enrollment tests
  E2E_USER_MFA_DISABLED: seedId('user-e2e-mfa-disabled', 101),
  // User with expired session for timeout tests
  E2E_USER_SESSION_TIMEOUT: seedId('user-e2e-timeout', 103),
  // User for password reset tests
  E2E_USER_PASSWORD_RESET: seedId('user-e2e-pw-reset', 105),
  // User for SSO tests
  E2E_USER_SSO: seedId('user-e2e-sso', 106),
  // User for Firebase exchange tests
  E2E_USER_FIREBASE: seedId('user-e2e-firebase', 107),
} as const;

export type AuthTestUserId = (typeof AUTH_TEST_USER_IDS)[keyof typeof AUTH_TEST_USER_IDS];

// =============================================================================
// CONSOLIDATED AUTH_SEED_IDS (Issue #277)
// =============================================================================

/**
 * All auth-related seed IDs consolidated for E2E scenarios
 *
 * @example
 * ```typescript
 * import { AUTH_SEED_IDS } from '@ops-matrix/shared-types';
 *
 * const apiKeyId = AUTH_SEED_IDS.apiKeys.E2E_TEST_KEY_1;
 * const sessionId = AUTH_SEED_IDS.sessions.E2E_ACTIVE_SESSION;
 * ```
 */
export const AUTH_SEED_IDS = {
  apiKeys: API_KEY_IDS,
  sessions: SESSION_IDS,
  mfaConfigs: MFA_CONFIG_IDS,
  mfaBackupCodes: MFA_BACKUP_CODE_IDS,
  ssoConfigs: SSO_CONFIG_IDS,
  testUsers: AUTH_TEST_USER_IDS,
} as const;

export type AuthSeedIds = typeof AUTH_SEED_IDS;

// =============================================================================
// API KEY DATA TYPE
// =============================================================================

export interface ApiKeySeedData {
  id: string;
  userId: string;
  businessId: string;
  name: string;
  keyPrefix: string;
  keyHash: string;
  scopes: string[];
  expiresAt?: string;
  isActive: boolean;
  lastUsedAt?: string;
  createdAt: string;
}

// =============================================================================
// SESSION DATA TYPE
// =============================================================================

export interface SessionSeedData {
  id: string;
  userId: string;
  businessId: string;
  deviceId?: string;
  ipAddress: string;
  userAgent: string;
  expiresAt: string;
  refreshToken?: string;
  isActive: boolean;
  createdAt: string;
}

// =============================================================================
// MFA CONFIGURATION DATA TYPE
// =============================================================================

export interface MfaConfigSeedData {
  id: string;
  userId: string;
  businessId: string;
  method: 'totp' | 'sms' | 'email';
  secret?: string; // Encrypted TOTP secret
  phoneNumber?: string;
  email?: string;
  isEnabled: boolean;
  verifiedAt?: string;
  createdAt: string;
}

// =============================================================================
// MFA BACKUP CODE DATA TYPE
// =============================================================================

export interface MfaBackupCodeSeedData {
  id: string;
  userId: string;
  codes: { code: string; usedAt?: string }[];
  generatedAt: string;
}

// =============================================================================
// SSO CONFIGURATION DATA TYPE
// =============================================================================

export interface SsoConfigSeedData {
  id: string;
  businessId: string;
  realmId: string;
  provider: 'saml' | 'oidc';
  name: string;
  entityId?: string; // SAML
  ssoUrl?: string;
  certificate?: string;
  clientId?: string; // OIDC
  clientSecret?: string;
  discoveryUrl?: string;
  isEnabled: boolean;
  createdAt: string;
}

// =============================================================================
// API KEY DATA
// =============================================================================

export const API_KEYS: Record<keyof typeof API_KEY_IDS, ApiKeySeedData> = {
  E2E_TEST_KEY_1: {
    id: API_KEY_IDS.E2E_TEST_KEY_1,
    userId: AUTH_TEST_USER_IDS.E2E_USER_MFA_ENABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'E2E Test Key 1',
    keyPrefix: 'om_e2e_test1',
    keyHash: '$2b$12$testKeyHash1XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX',
    scopes: ['read', 'write'],
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  E2E_TEST_KEY_2: {
    id: API_KEY_IDS.E2E_TEST_KEY_2,
    userId: AUTH_TEST_USER_IDS.E2E_USER_MFA_ENABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'E2E Test Key 2',
    keyPrefix: 'om_e2e_test2',
    keyHash: '$2b$12$testKeyHash2YYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYY',
    scopes: ['read'],
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  E2E_ROTATE_KEY: {
    id: API_KEY_IDS.E2E_ROTATE_KEY,
    userId: AUTH_TEST_USER_IDS.E2E_USER_MFA_ENABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'E2E Rotation Test Key',
    keyPrefix: 'om_e2e_rotate',
    keyHash: '$2b$12$rotateKeyHashZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZ',
    scopes: ['read', 'write'],
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  E2E_REVOKE_KEY: {
    id: API_KEY_IDS.E2E_REVOKE_KEY,
    userId: AUTH_TEST_USER_IDS.E2E_USER_MFA_ENABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'E2E Revocation Test Key',
    keyPrefix: 'om_e2e_revoke',
    keyHash: '$2b$12$revokeKeyHashAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
    scopes: ['read', 'write'],
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  RACHEL_API_KEY: {
    id: API_KEY_IDS.RACHEL_API_KEY,
    userId: USER_IDS.RACHEL_MARTINEZ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: "Rachel's API Key",
    keyPrefix: 'om_rachel',
    keyHash: '$2b$12$rachelKeyHashBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBB',
    scopes: ['read', 'write', 'admin'],
    isActive: true,
    lastUsedAt: '2026-01-21T14:30:00Z',
    createdAt: '2025-06-01T00:00:00Z',
  },
  SUPER_ADMIN_API_KEY: {
    id: API_KEY_IDS.SUPER_ADMIN_API_KEY,
    userId: USER_IDS.SUPER_ADMIN,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    name: 'Super Admin API Key',
    keyPrefix: 'om_superadmin',
    keyHash: '$2b$12$superAdminKeyHashCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCCC',
    scopes: ['read', 'write', 'admin', 'platform'],
    isActive: true,
    lastUsedAt: '2026-01-22T08:00:00Z',
    createdAt: '2025-01-01T00:00:00Z',
  },
  SERVICE_ACCOUNT_KEY: {
    id: API_KEY_IDS.SERVICE_ACCOUNT_KEY,
    userId: USER_IDS.SUPER_ADMIN,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    name: 'Service Account Key',
    keyPrefix: 'om_service',
    keyHash: '$2b$12$serviceKeyHashDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDD',
    scopes: ['service'],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
  },
};

// =============================================================================
// SESSION DATA
// =============================================================================

export const SESSIONS: Record<keyof typeof SESSION_IDS, SessionSeedData> = {
  E2E_ACTIVE_SESSION: {
    id: SESSION_IDS.E2E_ACTIVE_SESSION,
    userId: AUTH_TEST_USER_IDS.E2E_USER_MFA_ENABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    ipAddress: '192.168.1.100',
    userAgent: 'E2E Test Agent',
    expiresAt: '2026-01-23T00:00:00Z',
    isActive: true,
    createdAt: '2026-01-22T00:00:00Z',
  },
  E2E_TIMEOUT_SESSION: {
    id: SESSION_IDS.E2E_TIMEOUT_SESSION,
    userId: AUTH_TEST_USER_IDS.E2E_USER_SESSION_TIMEOUT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    ipAddress: '192.168.1.101',
    userAgent: 'E2E Test Agent',
    expiresAt: '2026-01-21T00:00:00Z', // Already expired
    isActive: false,
    createdAt: '2026-01-20T00:00:00Z',
  },
  RACHEL_SESSION: {
    id: SESSION_IDS.RACHEL_SESSION,
    userId: USER_IDS.RACHEL_MARTINEZ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    ipAddress: '10.0.0.50',
    userAgent: 'Chrome/Mac',
    expiresAt: '2026-01-22T18:00:00Z',
    isActive: true,
    createdAt: '2026-01-22T08:00:00Z',
  },
  SUPER_ADMIN_SESSION: {
    id: SESSION_IDS.SUPER_ADMIN_SESSION,
    userId: USER_IDS.SUPER_ADMIN,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    ipAddress: '10.0.0.1',
    userAgent: 'Firefox/Linux',
    expiresAt: '2026-01-22T16:00:00Z',
    isActive: true,
    createdAt: '2026-01-22T08:00:00Z',
  },
};

// =============================================================================
// MFA CONFIGURATION DATA
// =============================================================================

export const MFA_CONFIGS: Record<keyof typeof MFA_CONFIG_IDS, MfaConfigSeedData> = {
  E2E_MFA_ENABLED: {
    id: MFA_CONFIG_IDS.E2E_MFA_ENABLED,
    userId: AUTH_TEST_USER_IDS.E2E_USER_MFA_ENABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    method: 'totp',
    secret: 'JBSWY3DPEHPK3PXP', // Test TOTP secret
    isEnabled: true,
    verifiedAt: '2026-01-01T00:00:00Z',
    createdAt: '2026-01-01T00:00:00Z',
  },
  E2E_MFA_DISABLED: {
    id: MFA_CONFIG_IDS.E2E_MFA_DISABLED,
    userId: AUTH_TEST_USER_IDS.E2E_USER_MFA_DISABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    method: 'totp',
    isEnabled: false,
    createdAt: '2026-01-01T00:00:00Z',
  },
  E2E_MFA_ENROLLMENT: {
    id: MFA_CONFIG_IDS.E2E_MFA_ENROLLMENT,
    userId: AUTH_TEST_USER_IDS.E2E_USER_MFA_DISABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    method: 'totp',
    secret: 'HXDMVJECJJWSRB3HWIZR4IFUGFTMXBOZ', // Pending verification
    isEnabled: false,
    createdAt: '2026-01-22T00:00:00Z',
  },
  RACHEL_MFA: {
    id: MFA_CONFIG_IDS.RACHEL_MFA,
    userId: USER_IDS.RACHEL_MARTINEZ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    method: 'totp',
    secret: 'KRMWSYJCOBUEGVZWOVBWU5KRGFZXS', // Rachel's TOTP secret
    isEnabled: true,
    verifiedAt: '2025-06-15T10:00:00Z',
    createdAt: '2025-06-15T09:55:00Z',
  },
  SUPER_ADMIN_MFA: {
    id: MFA_CONFIG_IDS.SUPER_ADMIN_MFA,
    userId: USER_IDS.SUPER_ADMIN,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    method: 'totp',
    secret: 'GEZDGNBVGY3TQOJQGEZDGNBVGY3TQOJQ', // Super Admin TOTP secret
    isEnabled: true,
    verifiedAt: '2025-01-01T00:00:00Z',
    createdAt: '2025-01-01T00:00:00Z',
  },
};

// =============================================================================
// MFA BACKUP CODE DATA
// =============================================================================

/**
 * MFA Backup Codes for E2E Testing
 * WARNING: These are hardcoded test backup codes - DO NOT use in production
 * Production backup codes must be cryptographically generated per-user
 */
export const MFA_BACKUP_CODES: Record<keyof typeof MFA_BACKUP_CODE_IDS, MfaBackupCodeSeedData> = {
  E2E_BACKUP_CODE_SET: {
    id: MFA_BACKUP_CODE_IDS.E2E_BACKUP_CODE_SET,
    userId: AUTH_TEST_USER_IDS.E2E_USER_MFA_ENABLED,
    codes: [
      { code: 'E2E-CODE-001' },
      { code: 'E2E-CODE-002' },
      { code: 'E2E-CODE-003' },
      { code: 'E2E-CODE-004' },
      { code: 'E2E-CODE-005' },
      { code: 'E2E-CODE-006', usedAt: '2026-01-15T10:00:00Z' }, // One used
      { code: 'E2E-CODE-007' },
      { code: 'E2E-CODE-008' },
      { code: 'E2E-CODE-009' },
      { code: 'E2E-CODE-010' },
    ],
    generatedAt: '2026-01-01T00:00:00Z',
  },
  RACHEL_BACKUP_CODES: {
    id: MFA_BACKUP_CODE_IDS.RACHEL_BACKUP_CODES,
    userId: USER_IDS.RACHEL_MARTINEZ,
    codes: [
      { code: 'RACHEL-001' },
      { code: 'RACHEL-002' },
      { code: 'RACHEL-003' },
      { code: 'RACHEL-004' },
      { code: 'RACHEL-005' },
      { code: 'RACHEL-006' },
      { code: 'RACHEL-007' },
      { code: 'RACHEL-008' },
    ],
    generatedAt: '2025-06-15T10:05:00Z',
  },
  SUPER_ADMIN_BACKUP_CODES: {
    id: MFA_BACKUP_CODE_IDS.SUPER_ADMIN_BACKUP_CODES,
    userId: USER_IDS.SUPER_ADMIN,
    codes: [
      { code: 'ADMIN-001' },
      { code: 'ADMIN-002' },
      { code: 'ADMIN-003' },
      { code: 'ADMIN-004' },
      { code: 'ADMIN-005' },
    ],
    generatedAt: '2025-01-01T00:05:00Z',
  },
};

// =============================================================================
// SSO CONFIGURATION DATA
// =============================================================================

export const SSO_CONFIGS: Record<keyof typeof SSO_CONFIG_IDS, SsoConfigSeedData> = {
  E2E_SAML_CONFIG: {
    id: SSO_CONFIG_IDS.E2E_SAML_CONFIG,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    provider: 'saml',
    name: 'E2E SAML Test IdP',
    entityId: 'https://e2e-idp.test/saml/metadata',
    ssoUrl: 'https://e2e-idp.test/saml/sso',
    certificate: 'MIIDXTCCAkWgAwIBAgIJ...TEST_CERT...==',
    isEnabled: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  OPSMATRIX_SAML: {
    id: SSO_CONFIG_IDS.OPSMATRIX_SAML,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    provider: 'saml',
    name: 'Fiops Capital SSO',
    entityId: 'https://idp.opsmatrix.local/saml/metadata',
    ssoUrl: 'https://idp.opsmatrix.local/saml/sso',
    certificate: 'MIIDXTCCAkWgAwIBAgIJ...OPSMATRIX_CERT...==',
    isEnabled: true,
    createdAt: '2025-06-01T00:00:00Z',
  },
  E2E_OIDC_CONFIG: {
    id: SSO_CONFIG_IDS.E2E_OIDC_CONFIG,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    provider: 'oidc',
    name: 'E2E OIDC Test IdP',
    clientId: 'e2e-test-client-id',
    clientSecret: 'e2e-test-client-secret',
    discoveryUrl: 'https://e2e-idp.test/.well-known/openid-configuration',
    isEnabled: true,
    createdAt: '2026-01-01T00:00:00Z',
  },
  OPSMATRIX_OIDC: {
    id: SSO_CONFIG_IDS.OPSMATRIX_OIDC,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    provider: 'oidc',
    name: 'Fiops Capital OIDC',
    clientId: 'opsmatrix-oidc-client',
    clientSecret: 'opsmatrix-oidc-secret',
    discoveryUrl: 'https://idp.opsmatrix.local/.well-known/openid-configuration',
    isEnabled: true,
    createdAt: '2025-06-01T00:00:00Z',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllApiKeys(): ApiKeySeedData[] {
  return Object.values(API_KEYS);
}

export function getApiKeysByUser(userId: string): ApiKeySeedData[] {
  return Object.values(API_KEYS).filter((k) => k.userId === userId);
}

export function getActiveApiKeys(): ApiKeySeedData[] {
  return Object.values(API_KEYS).filter((k) => k.isActive);
}

export function getAllSessions(): SessionSeedData[] {
  return Object.values(SESSIONS);
}

export function getAuthSessionsByUser(userId: string): SessionSeedData[] {
  return Object.values(SESSIONS).filter((s) => s.userId === userId);
}

export function getActiveSessions(): SessionSeedData[] {
  return Object.values(SESSIONS).filter((s) => s.isActive);
}

export function getAllMfaConfigs(): MfaConfigSeedData[] {
  return Object.values(MFA_CONFIGS);
}

export function getMfaConfigByUser(userId: string): MfaConfigSeedData | undefined {
  return Object.values(MFA_CONFIGS).find((c) => c.userId === userId && c.isEnabled);
}

export function getEnabledMfaConfigs(): MfaConfigSeedData[] {
  return Object.values(MFA_CONFIGS).filter((c) => c.isEnabled);
}

export function getAllMfaBackupCodes(): MfaBackupCodeSeedData[] {
  return Object.values(MFA_BACKUP_CODES);
}

export function getMfaBackupCodesByUser(userId: string): MfaBackupCodeSeedData | undefined {
  return Object.values(MFA_BACKUP_CODES).find((c) => c.userId === userId);
}

export function getAllSsoConfigs(): SsoConfigSeedData[] {
  return Object.values(SSO_CONFIGS);
}

export function getSsoConfigsByBusiness(businessId: string): SsoConfigSeedData[] {
  return Object.values(SSO_CONFIGS).filter((c) => c.businessId === businessId);
}

export function getSamlConfigs(): SsoConfigSeedData[] {
  return Object.values(SSO_CONFIGS).filter((c) => c.provider === 'saml');
}

export function getOidcConfigs(): SsoConfigSeedData[] {
  return Object.values(SSO_CONFIGS).filter((c) => c.provider === 'oidc');
}
